import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api.service';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Button,
  TextField,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FeaturesIcon from '@mui/icons-material/Extension';
import RefreshIcon from '@mui/icons-material/Refresh';
import PaletteIcon from '@mui/icons-material/Palette';
import { format } from 'date-fns';
import FeaturedPlayListIcon from '@mui/icons-material/FeaturedPlayList';

const TenantManagement = () => {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTenants, setSelectedTenants] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  // Transform array data to object format
  const transformTenantData = (data) => {
    if (!Array.isArray(data)) return [];
    
    return data.map(tenant => {
      // Check if tenant is already an array (nested array structure)
      const tenantArray = Array.isArray(tenant) ? tenant : tenant;
      
      // Map array values to object properties
      return {
        id: tenantArray[0], // Assuming first element is the ID/tenant_code
        tenant_alias: tenantArray[0],
        tenant_name: tenantArray[1],
        si_id: tenantArray[2],
        active: tenantArray[3] === 'active',
        created_at: tenantArray[4],
        updated_at: tenantArray[5]
      };
    });
  };

  // Fetch tenants from the backend
  const fetchTenants = async () => {
    setLoading(true);
    try {
      const response = await apiService.get('/admin/developer/tenantlist', {
        si_id: 'SI-HIOSAI-001'
      });
      
      // Transform the response data
      const transformedData = transformTenantData(response);
      setTenants(transformedData);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch tenants:', err);
      setError('Failed to load tenants. Please try again.');
      setTenants([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTenants();
  }, []);

  // Handle tenant selection
  const handleSelectTenant = (tenantId) => {
    setSelectedTenants(prev => {
      if (prev.includes(tenantId)) {
        return prev.filter(id => id !== tenantId);
      } else {
        return [...prev, tenantId];
      }
    });
  };

  // Handle select all tenants
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedTenants(filteredTenants.map(tenant => tenant.id));
    } else {
      setSelectedTenants([]);
    }
  };

  // Navigate to tenant features page
  const handleManageFeatures = (tenantAlias) => {
    console.log('tenantAlias:', tenantAlias);
    if (tenantAlias) {
      navigate(`/developers/tenant-features/${tenantAlias}`);
    } else {
      console.error('Tenant alias is undefined');
    }
  };

  // Filter tenants based on search term
  const filteredTenants = tenants.filter(tenant => 
    (tenant.tenant_name ? tenant.tenant_name.toLowerCase() : '').includes(searchTerm.toLowerCase()) ||
    (tenant.tenant_code ? tenant.tenant_code.toLowerCase() : '').includes(searchTerm.toLowerCase())
  );

  const columns = [
    {
      field: 'actions',
      headerName: 'Actions',
      width: 250,
      renderCell: (params) => (
        <Box>

          <IconButton 
            color="info" 
            onClick={() => navigate(`/developers/feature-flags/${params.row.tenant_alias}`)}
            title="Feature Flags"
          >
            <FeaturedPlayListIcon />
          </IconButton>
          <IconButton 
            color="success" 
            onClick={() => navigate(`/developers/theme-customizer/${params.row.tenant_alias}`)}
            title="Theme Customizer"
          >
            <PaletteIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Tenant Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button 
            variant="outlined" 
            startIcon={<RefreshIcon />}
            onClick={fetchTenants}
          >
            Refresh
          </Button>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={() => navigate('/developers/tenant-create')}
          >
            Add Tenant
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          variant="outlined"
          label="Search Tenants"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by tenant name or code"
        />
      </Box>

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 250px)' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedTenants.length > 0 && selectedTenants.length < filteredTenants.length}
                    checked={filteredTenants.length > 0 && selectedTenants.length === filteredTenants.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>Tenant Name</TableCell>
                <TableCell>Tenant Alias</TableCell>
                <TableCell>SI ID</TableCell>
                <TableCell>Created At</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress size={40} />
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Loading tenants...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filteredTenants.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body1">
                      No tenants found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredTenants.map((tenant) => (
                  <TableRow 
                    key={tenant.id}
                    hover
                    selected={selectedTenants.includes(tenant.id)}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedTenants.includes(tenant.id)}
                        onChange={() => handleSelectTenant(tenant.id)}
                      />
                    </TableCell>
                    <TableCell>{tenant.tenant_name}</TableCell>
                    <TableCell>{tenant.tenant_alias}</TableCell>
                    <TableCell>{tenant.si_id}</TableCell>
                    <TableCell>
                      {tenant.created_at ? format(new Date(tenant.created_at), 'MMM dd, yyyy') : 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={tenant.active ? "Active" : "Inactive"} 
                        color={tenant.active ? "success" : "default"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                        <Tooltip title={`Manage Features: ${tenant.tenant_alias}`}>
                          <IconButton 
                            color="primary"
                            onClick={() => {
                              console.log('Navigating to features for tenant:', tenant.tenant_alias);
                              handleManageFeatures(tenant.tenant_alias);
                            }}
                          >
                            <FeaturesIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit Tenant">
                          <IconButton 
                            color="info"
                            onClick={() => navigate(`/developers/tenant-edit/${tenant.tenant_alias}`)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Theme Customizer">
                          <IconButton 
                            color="success"
                            onClick={() => navigate(`/developers/theme/${tenant.tenant_alias}`)}
                          >
                            <PaletteIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Tenant">
                          <IconButton 
                            color="error"
                            // Add delete functionality here
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {selectedTenants.length > 0 && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2">
            {selectedTenants.length} tenant(s) selected
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button 
              variant="contained" 
              color="primary"
              startIcon={<FeaturesIcon />}
              onClick={() => {
                if (selectedTenants.length === 1) {
                  handleManageFeatures(selectedTenants[0]);
                } else {
                  // Handle bulk feature management
                  navigate(`/developers/tenant-features-bulk`, { 
                    state: { tenantIds: selectedTenants } 
                  });
                }
              }}
            >
              Manage Features
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default TenantManagement; 